import { API_CONFIG } from '@/config/api'
import { baseService } from './baseService'
import type { ActivityKind } from '@/components/researcher/dashboardTypes'

export interface DashboardKpis {
  active_studies: { value: number; delta: number; spark: number[] }
  responses_this_week: { value: number; delta_pct: number | null; spark: number[] }
  avg_completion: { value: number | null; delta_pp: number | null; spark: number[] }
}

export type AttentionReason =
  | 'resultsReady'
  | 'lowCompletion'
  | 'noResponses'
  | 'stalledRecruitment'
  | 'draftPending'

export interface DashboardAttentionItem {
  id: number
  name: string
  reason: AttentionReason
  detail: { pct?: number; n?: number; days?: number; target?: number }
  status: 'active' | 'review' | 'draft'
  action: 'viewResults' | 'open' | 'resume'
}

export interface DashboardActivityEvent {
  id: string
  kind: ActivityKind
  who: string
  target?: string
  count?: number
  /** Respondent target for `reachedRespondents` events. */
  target_n?: number
  /** Study the event belongs to. */
  project_id?: number
  /** ISO timestamp of the event. */
  at: string
}

export interface ActivityFeed {
  days: number
  events: DashboardActivityEvent[]
}

export interface DashboardData {
  kpis: DashboardKpis
  needs_attention: DashboardAttentionItem[]
  recent_activity: DashboardActivityEvent[]
}

export class DashboardService {
  async getDashboard(): Promise<DashboardData> {
    const response = await baseService.get<DashboardData>(API_CONFIG.ENDPOINTS.PROJECTS.DASHBOARD)
    return response.data
  }

  /** Full activity feed for the Activity tab (default: last 30 days). */
  async getActivity(days = 30): Promise<ActivityFeed> {
    const response = await baseService.get<ActivityFeed>(
      `${API_CONFIG.ENDPOINTS.PROJECTS.ACTIVITY}?days=${days}`,
    )
    return response.data
  }
}

export const dashboardService = new DashboardService()
